import { InitMsalConfig } from "./InitMsalConfig";
import * as React from "react";
import LoadingScreen from "./LoadingScreen";
import {
  Card,
  CardHeader,
  Body1,
  Field,
  Input,
  Label,
  Button,
  makeStyles,
  mergeClasses,
  Link,
  Persona,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { useStore, log } from "../index";
import { useAxios } from "../providers/AxiosProvider";
import { IClausionStateStore } from "../../zustand/store";
import { useShallow } from "zustand/shallow";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { useMsalDynamic } from "../providers/MsalDynamicProvider";
import { Attach20Filled, Info20Regular, PlugConnectedRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import globalStyles from "./CommonStyle";

const inputStyles = makeStyles({
  connectionUrlLbl: {
    marginTop: "9px",
  },

  personaWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "tokens.spacingHorizontalM",
  },
  serviceUrl: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginTop: "-8px",
  },
});

const ConnectionCard: React.FC = () => {
  const { t } = useTranslation();
  const { axiosInstance } = useAxios();

  const [isLoading, setIsLoading] = React.useState(false);

  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const name = activeAccount?.name;
  const username = activeAccount?.username;

  const [tmpServiceUrl, setTmpServiceUrl] = React.useState("");
  const serviceUrl = useStore(useShallow((state: IClausionStateStore) => state.serviceUrl));
  const [setServiceUrl] = useStore(useShallow((state: IClausionStateStore) => [state.setServiceUrl]));
  const [setScope] = useStore(useShallow((state: IClausionStateStore) => [state.setScope]));

  const addRefreshMode = useStore(useShallow((state: IClausionStateStore) => state.setRefreshMode));

  React.useEffect(() => {
    setTmpServiceUrl(serviceUrl);
  }, [serviceUrl]);

  const { setMsalInstance, createMsalInstance } = useMsalDynamic();

  const handleConnect = async () => {
    try {
      setServiceUrl(tmpServiceUrl);
      setIsLoading(true);
      // We must use getState() to return the current state of the serviceUrl. React update serviceUrl on the next render cycle, but we need the current value immediately.
      const { msalConfig, scope } = await InitMsalConfig(axiosInstance, useStore.getState().serviceUrl);
      setScope(scope);

      const msalInstance = createMsalInstance(msalConfig);
      await msalInstance.initialize().then(async () => {
        const loginResponse = await msalInstance.loginPopup({
          scopes: [scope],
          prompt: "select_account",
        });
        if (loginResponse.account) {
          msalInstance.setActiveAccount(loginResponse.account);
        }
        setMsalInstance(msalInstance);
        addRefreshMode("Sheet");
      });
    } catch (error) {
      console.error(error);
      onClickSignOutButton();
    } finally {
      setIsLoading(false);
    }
  };

  const onClickConnectButton = () => {
    if (tmpServiceUrl != "") {
      handleConnect();
    }
  };

  const { msalInstance } = useMsalDynamic();
  const onClickSignOutButton = () => {
    log.info(`Logged Out User: ${username}`);
    async function signOut() {
      if (msalInstance) {
        await msalInstance.logoutRedirect({
          onRedirectNavigate: () => false,
        });
      }
    }
    signOut();
    setServiceUrl("");
    setMsalInstance(null);
  };

  const styles = inputStyles();
  const globalStyle = globalStyles();

  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false); // State for tooltip visibility
  const buttonRef = React.useRef<HTMLButtonElement>(null); // Ref for the button

  // Handle clicks outside the button to close the tooltip
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    if (isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible]);

  return (
  <Card className={mergeClasses(globalStyle.cardContainer, globalStyle.root)}>
      <CardHeader
        header={
          <UnauthenticatedTemplate>
            <Body1 className={globalStyle.headerContainer}>
              {/* Group the icon and title together */}
              <span className={globalStyle.titleContainer}>
                <PlugConnectedRegular />
                <span className={mergeClasses(globalStyle.fontStyle, globalStyle.font16)}>{t("connection_title")}</span>
              </span>
              {/* <Tooltip
              content={
                <>
                  {t('connection_help_text')}{" "}
                  <Link className={globalStyle.anchorDetail} href={t('connection_help_url')}>{t('learn_more')}</Link>
                </>
              }
              relationship="description"
              visible={isTooltipVisible}
            >
              <Button
                ref={buttonRef} // Attach ref to the button
                appearance="transparent"
                icon={<Info20Regular />}
                size="small" // Reduce button size
                onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
              />
            </Tooltip> */}
              <Popover>
                <PopoverTrigger disableButtonEnhancement>
                  <Button
                    ref={buttonRef} // Attach ref to the button
                    appearance="transparent"
                    icon={<Info20Regular />}
                    size="small" // Reduce button size
                    onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
                  />
                </PopoverTrigger>

                <PopoverSurface className={globalStyle.popoverSurface}>
                  <div>
                    <p>
                      {t("connection_help_text")}{" "}
                    </p>
                  </div>
                </PopoverSurface>
              </Popover>
            </Body1>
          </UnauthenticatedTemplate>
        }
      />
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <AuthenticatedTemplate>
            <div className={styles.personaWrapper}>
              <Persona
                size="extra-large"
                textAlignment="center"
                name={name}
                secondaryText={username}
                tertiaryText={
                  <Link className={globalStyle.signOutLink} onClick={() => onClickSignOutButton()}>
                    {t("connection_sign_out")}
                  </Link>
                }
              />
            </div>
            <Label className={mergeClasses(globalStyle.fontStyle, globalStyle.font12, styles.connectionUrlLbl)}>
              {t("connection_current_service_address")}
            </Label>
            <span className={mergeClasses(globalStyle.fontStyle, globalStyle.fontBold14, styles.serviceUrl)}> 
              <Attach20Filled />
              {serviceUrl}
            </span>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <Field label={t("connection_unauthorized_label")}>
              <Input
                value={tmpServiceUrl}
                placeholder={t("connection_example_url")}
                onChange={(e) => setTmpServiceUrl(e.target.value)}
              />
            </Field>
            <Button
              className={globalStyle.greenButton}
              onClick={() => onClickConnectButton()}
              disabled={!tmpServiceUrl}
            >
              {t("connection_sign_in")}
            </Button>
          </UnauthenticatedTemplate>
        </>
      )}
    </Card>
  );
};

export default ConnectionCard;
